// src/tenantAdmin/membershipRefusal.js
// The "this person belongs elsewhere" refusals of the tenant-admin user routes.
//
// Deactivate, reset password and edit profile all answer 409 when the account
// reaches beyond this workspace. The backend checks the wider scope FIRST, so
// USER_HAS_OTHER_MEMBERSHIPS (the person belongs to other COMPANIES) takes
// precedence over USER_HAS_OTHER_TENANTS (other WORKSPACES) — this helper keeps
// that order.
//
// ⛔ The bodies carry `other_tenants` / `other_customers`: those are IDs, never
//    names, and are never rendered.
// ⛔ `err.message` is the API's English jargon and is never rendered either.
// ⚠️ The remedy is NOT a property of the refusal, it is a property of the ACTION:
//    a deactivation can be unblocked by the tenant-admin themselves (remove the
//    person from this workspace), while a password reset or a profile edit cannot
//    — those need an administrator of every company the person belongs to, or a
//    platform administrator. Hence one key per (code, action) pair.
// ⚠️ USER_HAS_OTHER_TENANTS copy must not claim another COMPANY: the listed
//    workspaces can be the caller's own retired ones.
// ⚠️ `activate`, `resend` and `email` share the `editProfile` remedy, and that is
//    a statement about the remedy, not laziness: reactivating an account, sending
//    it a fresh invitation link and correcting the address it signs in with all
//    write the shared ACCOUNT, so — exactly like a profile edit — the tenant-admin
//    cannot unblock them by touching this workspace. Only `deactivate` can be
//    unblocked here (remove the person from the workspace instead), which is why
//    it is the one action with copy of its own.

// Keyed by action. `deactivate` + USER_HAS_OTHER_TENANTS deliberately keeps the
// pre-existing `otherTenantsAlert` string (the only one of the six that already
// had copy).
const OTHER_MEMBERSHIPS_KEYS = {
  deactivate: 'tenantUsers.otherMemberships.deactivate',
  resetPassword: 'tenantUsers.otherMemberships.resetPassword',
  editProfile: 'tenantUsers.otherMemberships.editProfile',
  activate: 'tenantUsers.otherMemberships.editProfile',
  resend: 'tenantUsers.otherMemberships.editProfile',
  email: 'tenantUsers.otherMemberships.editProfile',
};

const OTHER_TENANTS_KEYS = {
  deactivate: 'tenantUsers.otherTenantsAlert',
  resetPassword: 'tenantUsers.otherTenants.resetPassword',
  editProfile: 'tenantUsers.otherTenants.editProfile',
  activate: 'tenantUsers.otherTenants.editProfile',
  resend: 'tenantUsers.otherTenants.editProfile',
  email: 'tenantUsers.otherTenants.editProfile',
};

/**
 * Our copy for a belongs-elsewhere refusal, or null when the error is something
 * else (the caller then keeps its own error handling).
 *
 * @param {(key: string) => string} t   i18n `t` bound to the `admin` namespace
 * @param {Error & {code?: string}} err the rejection, built by buildApiError
 * @param {'deactivate'|'resetPassword'|'editProfile'|'activate'|'resend'|'email'} action which write refused
 * @returns {string|null}
 */
export function membershipRefusalCopy(t, err, action) {
  if (!err || !err.code) return null;
  // An action with no entry (role change and remove never refuse this way)
  // returns null rather than a wrong remedy.
  if (err.code === 'USER_HAS_OTHER_MEMBERSHIPS') {
    const key = OTHER_MEMBERSHIPS_KEYS[action];
    return key ? t(key) : null;
  }
  if (err.code === 'USER_HAS_OTHER_TENANTS') {
    const key = OTHER_TENANTS_KEYS[action];
    return key ? t(key) : null;
  }
  return null;
}

export default membershipRefusalCopy;
